import type { NetCancellationStatistics } from '@/types/NetCancellationStatistics';

export interface NetCancellationBreakdown {
  totalScheduledCases: number;
  cancelledCases: number;
  netCancelledCases: number;
  rescheduledCases: number;
  otherScheduledCases: number;
  rate: number | null;
}

/** Presentation arithmetic only. Source inclusion, case identity and dates belong to the API. */
export function getNetCancellationBreakdown(
  statistics?: NetCancellationStatistics | null
): NetCancellationBreakdown | null {
  if (statistics?.availability !== 'AVAILABLE' || !statistics.counts) return null;

  const { totalScheduledCases, cancelledCases, rescheduledCases } = statistics.counts;
  // Do not turn missing/partial/invalid data into a plausible-looking zero or clamp it.
  if (
    ![totalScheduledCases, cancelledCases, rescheduledCases].every(n => Number.isSafeInteger(n) && n >= 0) ||
    cancelledCases > totalScheduledCases ||
    rescheduledCases > cancelledCases
  ) {
    return null;
  }

  const netCancelledCases = cancelledCases - rescheduledCases;
  return {
    totalScheduledCases,
    cancelledCases,
    netCancelledCases,
    rescheduledCases,
    otherScheduledCases: totalScheduledCases - cancelledCases,
    rate: totalScheduledCases === 0 ? null : (netCancelledCases / totalScheduledCases) * 100,
  };
}
